// @file Custom AJAX implementation that doesn't need jQuery

import getCsrfToken from './csrf_token'

let oauthToken: string | null | undefined = null

function setOauthToken(token: string | null | undefined) {
  oauthToken = token
}

interface CustomXHR extends XMLHttpRequest {
  abort: () => void
}

interface AjaxOptions {
  url: string
  type?: string
  method?: string
  data?: any
  headers?: Record<string, string>
  cache?: boolean
  contentType?: string | false
  processData?: boolean
  dataType?: string
  crossDomain?: boolean
  timeout?: number
  xhr?: () => XMLHttpRequest
  success?: (response: any) => void
  error?: (response: any, textStatus?: string) => void
  sendOauthToken?: boolean
}

function customAjax(options: AjaxOptions): CustomXHR {
  const xhr = options.xhr?.() ?? new XMLHttpRequest()

  // Set up timeout if specified
  if (options.timeout != null) {
    xhr.timeout = options.timeout
  }

  // Method setup
  const method = (options.type || options.method || 'GET').toUpperCase()

  // URL setup
  let url = options.url
  if (options.cache === false && method === 'GET') {
    url += (url.includes('?') ? '&' : '?') + '_=' + Date.now()
  }

  // Handle GET request with data
  if (method === 'GET' && options.data) {
    // Filter out undefined/null values to prevent them from being converted to strings
    const filteredData = Object.fromEntries(
      Object.entries(options.data).filter(([_, value]) => value !== undefined && value !== null),
    ) as Record<string, string>
    const params = new URLSearchParams(filteredData).toString()
    url += (url.includes('?') ? '&' : '?') + params
  }

  xhr.open(method, url, true)

  // Set headers
  if (options.headers != null) {
    Object.entries(options.headers).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value)
    })
  }

  // Add CSRF token for non-GET requests
  if (method !== 'GET') {
    xhr.setRequestHeader('X-CSRF-Token', getCsrfToken())
  }

  // Add OAuth token if requested
  if (oauthToken && options.sendOauthToken) {
    xhr.setRequestHeader('Authorization', `Bearer ${oauthToken}`)
  }

  if (options.contentType !== false) {
    xhr.setRequestHeader('Content-Type', options.contentType || 'application/x-www-form-urlencoded; charset=UTF-8')
  }

  // Handle response
  xhr.onload = function () {
    if (xhr.status >= 200 && xhr.status < 300) {
      const response = options.dataType === 'json' ? JSON.parse(xhr.responseText) : xhr.responseText
      options.success?.(response)
    } else {
      options.error?.(xhr, xhr.statusText)
    }
  }

  xhr.onerror = function () {
    options.error?.(xhr, xhr.statusText)
  }

  // Send data
  let dataToSend = options.data
  if (method !== 'GET') {
    if (dataToSend instanceof FormData) {
      // Keep FormData as-is
    } else if (typeof dataToSend === 'object') {
      // Convert object to comma-separated string
      dataToSend = Object.entries(dataToSend)
        // remove undefined/null values
        .filter(([_, value]) => value !== undefined && value !== null)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value as string | number | boolean)}`)
        .join('&')
    }
  }

  xhr.send(dataToSend)
  return xhr as CustomXHR
}
export { setOauthToken, customAjax }
export type { CustomXHR, AjaxOptions }
